import React, { useState, useEffect } from 'react';
import { Card, Typography, Progress, Row, Col, Alert, Spin, Badge, Divider, List } from 'antd';
import { WarningOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import axios from '../api';
import { calculateKDIGORisk } from '../utils/kdigoRiskStratification';

const { Title, Text, Paragraph } = Typography;

const KidneyDiseaseScore = ({ patientId }) => {
  const [riskData, setRiskData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPatientData = async () => {
      if (!patientId) return;

      setLoading(true);
      setError(null);

      try {
        // Fetch flowsheet data (KDIGO only needs eGFR and ACR)
        const flowsheetResponse = await axios.get(`/patients/${patientId}/flowsheet/data`);
        const responseData = flowsheetResponse.data.data;

        // The actual flowsheet entries are in flowsheetData array
        const flowsheetData = responseData.flowsheetData;

        // Helper to find the latest value for a given parameter from flowsheet entries
        const getLatestValue = (entries, parameterKey) => {
          if (!entries || !Array.isArray(entries)) {
            return null;
          }

          const matchingEntries = entries.filter(d => d.name === parameterKey);
          if (matchingEntries.length === 0) return null;

          // Sort by entryDate descending and get the first one
          matchingEntries.sort((a, b) => new Date(b.entryDate) - new Date(a.entryDate));
          return parseFloat(matchingEntries[0].value);
        };

        const eGFR = getLatestValue(flowsheetData, 'labs_renal_egfr');
        const albuminCreatinineRatio = getLatestValue(flowsheetData, 'labs_urine_uacr');

        // Check if we have the required data
        if (!eGFR || !albuminCreatinineRatio) {
          setError('Missing required lab values: eGFR and/or Albumin-Creatinine Ratio (ACR) not found.');
          setRiskData(null);
          setLoading(false);
          return;
        }

        const params = {
          eGFR,
          albuminCreatinineRatio
        };

        const result = calculateKDIGORisk(params);
        
        if (result.error) {
          setError(result.error);
          setRiskData(null);
        } else {
          setRiskData(result);
        }

      } catch (err) {
        console.error('Error fetching patient lab data:', err);
        setError(`Failed to fetch patient lab data: ${err.response?.data?.message || err.message || 'Unknown error'}`);
        setRiskData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPatientData();
  }, [patientId]);

  if (loading) {
    return <Spin tip="Calculating Kidney Disease Risk..." />;
  }

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  if (!riskData) {
    return <Alert message="Insufficient Data" description="Not enough data to calculate kidney disease risk." type="warning" showIcon />;
  }

  // Get risk icon based on level
  const getRiskIcon = (level) => {
    switch (level) {
      case 'low':
        return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />;
      case 'moderate':
        return <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 24 }} />;
      case 'high':
      case 'veryHigh':
        return <WarningOutlined style={{ color: '#f5222d', fontSize: 24 }} />;
      default:
        return null;
    }
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>KDIGO CKD Risk Stratification</span>
          <Badge
            count={riskData.riskCategory}
            style={{ backgroundColor: riskData.riskColor }}
          />
        </div>
      }
    >
      <Row gutter={[24, 24]}>
        {/* Left Column - Risk Summary & Key Metrics */}
        <Col xs={24} lg={10}>
          {/* Risk Summary Card */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 15,
            padding: '20px',
            backgroundColor: '#fafafa',
            borderRadius: '8px',
            marginBottom: 20
          }}>
            {getRiskIcon(riskData.riskLevel)}
            <div>
              <Title level={4} style={{ margin: 0, marginBottom: 4 }}>{riskData.riskCategory}</Title>
              <Text type="secondary">{riskData.riskDescription}</Text>
            </div>
          </div>

          {/* Key Metrics */}
          <Row gutter={[12, 12]}>
            <Col span={24}>
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#f0f5ff',
                borderRadius: '6px',
                borderLeft: '4px solid #1890ff'
              }}>
                <Text strong style={{ fontSize: '12px', color: '#666' }}>CKD STAGE</Text>
                <div style={{ marginTop: 4 }}>
                  <Text strong style={{ fontSize: '16px' }}>{riskData.ckdStage}</Text>
                </div>
              </div>
            </Col>

            <Col span={12}>
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#f6ffed',
                borderRadius: '6px',
                borderLeft: '4px solid #52c41a'
              }}>
                <Text strong style={{ fontSize: '11px', color: '#666' }}>eGFR</Text>
                <div style={{ marginTop: 4 }}>
                  <Text strong style={{ fontSize: '18px' }}>{riskData.gfrCategory.value}</Text>
                  <Text type="secondary" style={{ fontSize: '11px', marginLeft: 4 }}>mL/min/1.73m²</Text>
                </div>
                <div style={{ marginTop: 2 }}>
                  <Text type="secondary" style={{ fontSize: '11px' }}>{riskData.gfrCategory.code}</Text>
                </div>
              </div>
            </Col>

            <Col span={12}>
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#fff7e6',
                borderRadius: '6px',
                borderLeft: '4px solid #faad14'
              }}>
                <Text strong style={{ fontSize: '11px', color: '#666' }}>ACR</Text>
                <div style={{ marginTop: 4 }}>
                  <Text strong style={{ fontSize: '18px' }}>{riskData.albuminuriaCategory.value}</Text>
                  <Text type="secondary" style={{ fontSize: '11px', marginLeft: 4 }}>mg/g</Text>
                </div>
                <div style={{ marginTop: 2 }}>
                  <Text type="secondary" style={{ fontSize: '11px' }}>{riskData.albuminuriaCategory.code}</Text>
                </div>
              </div>
            </Col>
          </Row>

          {/* Source Info - Compact */}
          <div style={{ marginTop: 20, padding: '12px', backgroundColor: '#fafafa', borderRadius: '6px' }}>
            <Text style={{ fontSize: '11px', color: '#666' }}>
              <strong>Source:</strong> {riskData.source}
            </Text>
          </div>
        </Col>

        {/* Right Column - Clinical Recommendations */}
        <Col xs={24} lg={14}>
          <Title level={5} style={{ marginBottom: 16 }}>Clinical Recommendations</Title>
          <List
            size="small"
            dataSource={riskData.recommendations}
            renderItem={(item) => (
              <List.Item style={{ padding: '8px 0', borderBottom: 'none' }}>
                <Text>• {item}</Text>
              </List.Item>
            )}
            style={{
              backgroundColor: '#f5f5f5',
              padding: '16px 20px',
              borderRadius: '8px',
              maxHeight: '320px',
              overflowY: 'auto'
            }}
          />

          {/* Risk Level Guide - Compact */}
          <div style={{ marginTop: 20 }}>
            <Title level={5} style={{ marginBottom: 12 }}>Risk Level Guide</Title>
            <div style={{ fontSize: '12px', lineHeight: '1.8' }}>
              <div>
                <Badge color="#52c41a" text={<Text strong>Low Risk</Text>} /> - Routine CKD management, annual monitoring
              </div>
              <div>
                <Badge color="#faad14" text={<Text strong>Moderate Risk</Text>} /> - Optimize management, monitor every 6 months
              </div>
              <div>
                <Badge color="#ff7a45" text={<Text strong>High Risk</Text>} /> - Nephrology referral recommended, monitor every 3-6 months
              </div>
              <div>
                <Badge color="#f5222d" text={<Text strong>Very High Risk</Text>} /> - Urgent nephrology referral, close monitoring, prepare for kidney replacement therapy
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Card>
  );
};

export default KidneyDiseaseScore;
